import { type IDropdownItem } from 'shared/ui'
import { EnumUserRoles } from 'entities/Users/model/types'

export const roleLabelsMap: Record<EnumUserRoles, string> = {
  [EnumUserRoles.OWNER]: 'Owner',
  [EnumUserRoles.ADMIN]: 'Admin',
  [EnumUserRoles.MANAGER]: 'Manager',
  [EnumUserRoles.MEMBER]: 'Member',
  [EnumUserRoles.VIEW_ONLY]: 'Observer',
}

export const assignableRoles = [
  EnumUserRoles.ADMIN,
  EnumUserRoles.MANAGER,
  EnumUserRoles.MEMBER,
  EnumUserRoles.VIEW_ONLY,
] as const

const toRoleDropdownItem = (role: EnumUserRoles): IDropdownItem => ({
  id: role,
  value: role,
  label: roleLabelsMap[role],
})

export const roleDropdownItems: IDropdownItem[] = assignableRoles.map(toRoleDropdownItem)

/**
 * Numeric privilege rank derived from the role hierarchy.
 * Higher number = more privileged. Used to prevent privilege escalation
 * when assigning roles during member invitation.
 */
export const roleRank: Record<EnumUserRoles, number> = {
  [EnumUserRoles.VIEW_ONLY]: 0,
  [EnumUserRoles.MEMBER]: 1,
  [EnumUserRoles.MANAGER]: 2,
  [EnumUserRoles.ADMIN]: 3,
  [EnumUserRoles.OWNER]: 4,
}

const MAX_ASSIGNABLE_RANK = roleRank[EnumUserRoles.ADMIN]

export const canAssignRole = (
  inviterRole: EnumUserRoles | undefined,
  targetRole: EnumUserRoles
): boolean => {
  if (!inviterRole) return false
  const cap = Math.min(roleRank[inviterRole], MAX_ASSIGNABLE_RANK)
  return roleRank[targetRole] <= cap
}

/**
 * Role dropdown items limited to the actor's own role level and below.
 * Owner/Admin -> Admin, Manager, Member, Observer
 * Manager     -> Manager, Member, Observer
 * Member      -> Member, Observer
 */
export const getAssignableRoleItems = (
  actorRole: EnumUserRoles | undefined,
  keepRole?: EnumUserRoles | null
): IDropdownItem[] =>
  assignableRoles
    .filter((role) => canAssignRole(actorRole, role) || role === keepRole)
    .map(toRoleDropdownItem)

export const getDefaultAssignableRole = (inviterRole: EnumUserRoles | undefined): EnumUserRoles => {
  if (canAssignRole(inviterRole, EnumUserRoles.MEMBER)) return EnumUserRoles.MEMBER

  const assignable = assignableRoles.filter((role) => canAssignRole(inviterRole, role))
  return assignable[assignable.length - 1] ?? EnumUserRoles.VIEW_ONLY
}
